import React, { useEffect, useMemo } from "react";
import { useTranslate, useCreate } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import {
  Form,
  Input,
  Row,
  Col,
  Select,
  Button,
  Modal,
  Divider,
  Typography,
  theme,
  Space,
} from "antd";
import {
  HomeOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  CreditCardOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { useSelectValues } from "../values";
import { AccountType, IAccount } from "../../../models/account";
import { useRequiresQontoInternationalPhone } from "../hooks/useRequiresQontoInternationalPhone";
import { AccountPhoneFormItem } from "./AccountPhoneFormItem";
import { getCountryOptions } from "../../../utils/countries";

const { Title } = Typography;

type AccountCreateModalProps = {
  open: boolean;
  onCancel: () => void;
  onSuccess?: (account?: IAccount) => void;
};

export const AccountCreateModal: React.FC<AccountCreateModalProps> = ({
  open,
  onCancel,
  onSuccess,
}) => {
  const translate = useTranslate();
  const { i18n } = useTranslation();
  const countryOptions = useMemo(
    () => getCountryOptions(i18n.language),
    [i18n.language],
  );
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const accountType = Form.useWatch("type", form);
  const { accountTypes, billingModes } = useSelectValues();
  const requiresInternationalPhone = useRequiresQontoInternationalPhone(
    null,
    accountType,
  );

  const { mutate: create, isLoading: isCreating } = useCreate();

  useEffect(() => {
    if (!open) {
      form.resetFields();
      form.setFieldsValue({
        type: accountTypes[0]?.value,
        billingMode: billingModes[0]?.value,
        status: "active",
      });
    }
  }, [open, form, accountTypes, billingModes]);

  const handleFinish = (values: any) => {
    create(
      {
        resource: "accounts",
        values,
      },
      {
        onSuccess: (data) => {
          form.resetFields();
          onCancel();
          onSuccess?.(data?.data as IAccount | undefined);
        },
      },
    );
  };

  return (
    <Modal
      title={translate("accounts.titles.create")}
      open={open}
      onCancel={onCancel}
      width={1000}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={onCancel}>{translate("buttons.cancel")}</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={isCreating}
            onClick={() => form.submit()}
          >
            {translate("buttons.save")}
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          type: accountTypes[0]?.value,
          billingMode: billingModes[0]?.value,
          status: "active",
        }}
        className="!mt-6"
      >
        <Form.Item name={["status"]} hidden>
          <Input />
        </Form.Item>

        <Title level={5} className="!mb-4 !mt-2">
          <HomeOutlined className="mr-2" style={{ color: token.colorPrimary }} />
          {translate("accounts.sections.companyInfo")}
        </Title>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item
              label={translate("accounts.fields.type")}
              name={["type"]}
              rules={[{ required: true }]}
            >
              <Select options={accountTypes} />
            </Form.Item>
          </Col>
          {accountType === AccountType.company ? (
            <>
              <Col xs={24} sm={10}>
                <Form.Item
                  label={translate("accounts.fields.name")}
                  name={["name"]}
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={6}>
                <Form.Item
                  label={translate("accounts.fields.legalId")}
                  name={["legalId"]}
                  rules={[
                    {
                      required: true,
                      message: translate(
                        "accounts.validation.legalIdRequiredForCompany",
                      ),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </>
          ) : (
            <>
              <Col xs={24} sm={8}>
                <Form.Item
                  label={translate("accounts.fields.firstName")}
                  name={["firstName"]}
                  rules={[
                    {
                      required: true,
                      message: translate(
                        "accounts.validation.firstNameRequiredForIndividual",
                      ),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  label={translate("accounts.fields.lastName")}
                  name={["lastName"]}
                  rules={[
                    {
                      required: true,
                      message: translate(
                        "accounts.validation.lastNameRequiredForIndividual",
                      ),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>

        <Divider className="!my-2" />

        <Title level={5} className="!mb-4 !mt-4">
          <EnvironmentOutlined
            className="mr-2"
            style={{ color: token.colorPrimary }}
          />
          {translate("accounts.sections.address")}
        </Title>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={translate("accounts.fields.address")}
              name={["address"]}
              rules={[
                {
                  required: true,
                  message: translate("accounts.validation.addressRequired"),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item
              label={translate("accounts.fields.zipCode")}
              name={["zipCode"]}
              rules={[
                {
                  required: true,
                  message: translate("accounts.validation.zipCodeRequired"),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item
              label={translate("accounts.fields.city")}
              name={["city"]}
              rules={[
                {
                  required: true,
                  message: translate("accounts.validation.cityRequired"),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item
              label={translate("accounts.fields.country")}
              name={["country"]}
              rules={[
                {
                  required: true,
                  message: translate("accounts.validation.countryRequired"),
                },
              ]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={countryOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider className="!my-2" />

        <Title level={5} className="!mb-4 !mt-4">
          <PhoneOutlined className="mr-2" style={{ color: token.colorPrimary }} />
          {translate("accounts.sections.contact")}
        </Title>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <AccountPhoneFormItem
              label={translate("accounts.fields.phone")}
              requiresInternationalFormat={requiresInternationalPhone}
              translate={translate}
            />
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label={translate("accounts.fields.email")}
              name={["email"]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Divider className="!my-2" />

        <Title level={5} className="!mb-4 !mt-4">
          <CreditCardOutlined
            className="mr-2"
            style={{ color: token.colorPrimary }}
          />
          {translate("accounts.sections.billing")}
        </Title>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={translate("accounts.fields.billingMode")}
              name={["billingMode"]}
              rules={[{ required: true }]}
            >
              <Select options={billingModes} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
